/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useMemo } from "react";

dayjs.extend(utc);
import {
  Event,
  LabellerDetails,
  LabellerTimeline,
} from "src/store/api/generatedApi";
import { ScrapEyesEventWithReviewData } from "../contexts/ManualReviewEventContext";
import { expandOriginalEvent } from "../utils/eventUtils";
import { ExpandedOriginalEvent } from "../types";
import { useAreaLabelledEvent } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";

export interface OriginalEventWrapper {
  event: Event;
  scrapEyesEvent: ExpandedOriginalEvent;
}

interface GroupedOriginalEvent {
  labellerDetails: LabellerDetails;
  events: OriginalEventWrapper[];
}

interface VideoTimelineData {
  videoStart: Dayjs;
  videoEnd: Dayjs;
  originalEventsByLabeller: GroupedOriginalEvent[];
  videoDuration: number;
}

const groupEventsByLabeller = (
  events: OriginalEventWrapper[]
): GroupedOriginalEvent[] => {
  const groupedHumanEvents: Record<string, OriginalEventWrapper[]> = {};
  const groupedModelEvents: Record<string, OriginalEventWrapper[]> = {};

  events.forEach((expandedEvent) => {
    if (
      expandedEvent.scrapEyesEvent.labellerDetails.labeller_type === "human"
    ) {
      const group = (groupedHumanEvents[
        expandedEvent.scrapEyesEvent.labellerDetails.id
      ] ??= []);
      group.push(expandedEvent);
    } else if (
      expandedEvent.scrapEyesEvent.labellerDetails.labeller_type === "model"
    ) {
      const group = (groupedModelEvents[
        expandedEvent.scrapEyesEvent.labellerDetails.id
      ] ??= []);
      group.push(expandedEvent);
    }
  });

  const humanGroups = Object.values(groupedHumanEvents)
    .sort((a, b) =>
      (a[0]?.scrapEyesEvent.labellerDetails.id ?? "").localeCompare(
        b[0]?.scrapEyesEvent.labellerDetails.id ?? ""
      )
    )
    .flatMap((eventList) => {
      const first = eventList[0];
      return first
        ? [
            {
              labellerDetails: first.scrapEyesEvent.labellerDetails,
              events: eventList,
            },
          ]
        : [];
    });
  const modelGroups = Object.values(groupedModelEvents)
    .sort(
      (a, b) =>
        Number(a[0]?.scrapEyesEvent.labellerDetails.id ?? 0) -
        Number(b[0]?.scrapEyesEvent.labellerDetails.id ?? 0)
    )
    .flatMap((eventList) => {
      const first = eventList[0];
      return first
        ? [
            {
              labellerDetails: first.scrapEyesEvent.labellerDetails,
              events: eventList,
            },
          ]
        : [];
    });
  return [...humanGroups, ...modelGroups];
};

export const useVideoTimelineData = (
  mergedEvent: ScrapEyesEventWithReviewData,
  eventToLabellerMapping: Record<number, LabellerDetails>,
  location: string | undefined
): VideoTimelineData => {
  const { default_areas: defaultAreas } = useScrapEyeMetadata();
  const withAreaLabels = useAreaLabelledEvent(location);
  const { original_events: originalEvents, id: mergedEventId } = mergedEvent;

  return useMemo(() => {
    const expandedEvents: OriginalEventWrapper[] = [];
    let minStart: Dayjs | null = null;
    let maxEnd: Dayjs | null = null;

    originalEvents.forEach((originalEvent) => {
      const labellerDetails = eventToLabellerMapping[originalEvent.id] ?? {
        id: "Unknown",
        name: null,
        email: null,
        labeller_type: "human",
        model_status: null,
      };
      const expandedScrapEyesEvent = withAreaLabels(
        expandOriginalEvent(
          originalEvent,
          defaultAreas,
          labellerDetails,
          mergedEventId,
          false
        )
      );

      expandedEvents.push({
        event: originalEvent,
        scrapEyesEvent: expandedScrapEyesEvent,
      });

      const { start, end } = expandedScrapEyesEvent;
      if (!minStart || start.isBefore(minStart)) minStart = start;
      if (!maxEnd || end.isAfter(maxEnd)) maxEnd = end;
    });

    minStart ??= mergedEvent.start;
    maxEnd ??= mergedEvent.end;

    const originalEventsByLabeller = groupEventsByLabeller(expandedEvents);

    const videoStartSeconds = minStart.subtract(5, "second").unix();
    const videoEndSeconds = maxEnd.add(5, "second").unix();

    const roundedStartSeconds = Math.floor(videoStartSeconds / 15) * 15;
    const roundedEndSeconds = Math.ceil(videoEndSeconds / 15) * 15;

    const videoStart = dayjs.unix(roundedStartSeconds).utc();
    const videoEnd = dayjs.unix(roundedEndSeconds).utc();
    const videoDuration = videoEnd.diff(videoStart, "second");

    return {
      videoStart,
      videoEnd,
      originalEventsByLabeller,
      videoDuration,
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mergedEvent is derived from originalEvents (already a dep), so its start/end are transitively covered
  }, [
    originalEvents,
    mergedEventId,
    defaultAreas,
    eventToLabellerMapping,
    withAreaLabels,
  ]);
};

export const useSurroundingEventsByLabeller = (
  labellerTimelines: LabellerTimeline[],
  windowStart: Dayjs,
  windowEnd: Dayjs,
  location: string | undefined
): GroupedOriginalEvent[] => {
  const { default_areas: defaultAreas } = useScrapEyeMetadata();
  const withAreaLabels = useAreaLabelledEvent(location);
  const windowStartMs = windowStart.valueOf();
  const windowEndMs = windowEnd.valueOf();
  return useMemo(() => {
    const wrappers: OriginalEventWrapper[] = [];
    labellerTimelines.forEach((timeline) => {
      timeline.periods.forEach((period) => {
        period.events.forEach((event) => {
          const startMs = dayjs.utc(event.data.start).valueOf();
          const endMs = dayjs.utc(event.data.end).valueOf();
          if (endMs < windowStartMs || startMs > windowEndMs) return;
          wrappers.push({
            event,
            scrapEyesEvent: withAreaLabels(
              expandOriginalEvent(event, defaultAreas, timeline.labeller)
            ),
          });
        });
      });
    });
    return groupEventsByLabeller(wrappers);
  }, [
    labellerTimelines,
    windowStartMs,
    windowEndMs,
    defaultAreas,
    withAreaLabels,
  ]);
};
